import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import axios from "axios";
import SwipeableViews from "react-swipeable-views-react-18-fix";
import PropTypes from "prop-types";
import ReactGA from "react-ga4";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  styled,
  Badge,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import config from "../../config";
import Headerbar from "../components/header";
import IncidentDetail from "./detail/incident_detail";
import ChatPage from "../chat_page";

function IncidentDetailPage({ data }) {
  const { id, tab } = useParams();

  //redux
  const state = useSelector((state) => {
    return state;
  });
  const { ticket } = state;
  const { userapi, user } = useSelector((state) => state);

  const [details, setDetails] = useState(null);
  const [status, setStatus] = useState(null);
  const [activity, setActivity] = useState([]);

  const jwt = user.jwt;

  // on load, filter data in redux store base on id
  useEffect(() => {
    const details = ticket.history.find(
      (item) => String(item.id) === String(id)
    );
    if (details) {
      const { status } = details;
      setDetails(details);
      setStatus(status);
    }
  }, [id, state, ticket]);

  useEffect(() => {
    fetchActivity();
  }, []);

  const theme = useTheme();
  const isActivityTab = tab === "activity";
  const [value, setValue] = useState(isActivityTab ? 1 : 0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleChangeIndex = (index) => {
    setValue(index);
  };

  const groupChatsByDate = (arr, property) => {
    arr.forEach((item) => {
      const date = new Date(item.datetime);
      const year = date.getFullYear();
      const month = date.toLocaleString("default", { month: "short" });
      const day = date.getDate().toString().padStart(2, "0");

      item.formattedDate = `${day} ${month} ${year}`;
    });
    return arr.reduce((accVal, currVal) => {
      var key = currVal[property];
      if (!accVal[key]) {
        accVal[key] = [];
      }
      accVal[key].push(currVal);
      return accVal;
    }, {});
  };

  const groupedSortedChats = useMemo(() => {
    if (!activity.length) return;
    const groupedChats = groupChatsByDate(activity, "formattedDate");
    const groupedAndSorted = Object.keys(groupedChats)
      .sort((a, b) => new Date(a) - new Date(b))
      .reduce((sortedAcc, key) => {
        sortedAcc[key] = groupedChats[key];
        return sortedAcc;
      }, {});
    return groupedAndSorted;
  }, [activity]);

  const hideActivityDot = useMemo(() => {
    return status !== "Pending Response";
  }, [status]);

  const fetchActivity = () => {
    axios
      .get(`${config.API_BASE_URL}userincidentdetails?sys_id=${id}`, {
        headers: {
          Authorization: `Bearer ${jwt}`,
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
        setActivity(res.data.data.comments);
      })
      .catch((e) => {
        console.error("Error fetching activity:", e);
        // setIsLoading(false);
        // setLoadError(true);
      });
  };

  const onTabClick = (tabName) => {
    ReactGA.gtag("event", `Tap ${tabName} tab`, {
      caseId: details.caseNumber,
      sessionIdCustom: userapi.sessionID,
      userId: userapi.userSysId,
      orgID: !userapi.orgId ? 0 : userapi.orgId,
      agency: userapi.agency,
      channel: "Mobile",
      timestamp: Date(),
    });
  };

  const StyledTabs = styled((props) => (
    <Tabs
      {...props}
      TabIndicatorProps={{
        children: <span className="MuiTabs-indicatorSpan" />,
      }}
    />
  ))({
    "& .MuiTabs-indicator": {
      display: "flex",
      justifyContent: "center",
      backgroundColor: "transparent",
    },
    "& .MuiTabs-indicatorSpan": {
      maxWidth: 60,
      width: "100%",
      backgroundColor: "#376EEE",
    },
    backgroundColor: "white",
    borderBottom: "1px solid #D0D0D0",
  });

  const StyledTab = styled((props) => <Tab disableRipple {...props} />)(
    ({ theme }) => ({
      textTransform: "none",
      fontWeight: theme.typography.fontWeightRegular,
      fontSize: 16,
      marginRight: theme.spacing(1),
      color: "#757575",
      "&.Mui-selected": {
        color: "#376EEE",
        fontWeight: 700,
      },
      "&.Mui-focusVisible": {
        backgroundColor: "rgba(100, 95, 228, 0.32)",
      },
    })
  );

  //when user has pending response at the activity tab
  const StyledBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
      right: -3,
      top: -2,
      border: `4px solid ${theme.palette.background.paper}`,
      padding: "0px",
      fontSize: "1px",
      backgroundColor: "#B3261E",
      color: "white",
    },
  }));

  // if no details will have a spinner
  if (!details) {
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={true}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  return (
    <motion.div
      sx={{ bgcolor: "background.paper" }}
      initial={{ x: 0 }}
      animate={{ width: "100%" }}
      exit={{ x: window.outerWidth, transition: { duration: 0.3 } }}
    >
      <Headerbar title={"Case Details"} backLink={!tab ? "" : "/"} />
      <StyledTabs
        value={value}
        onChange={handleChange}
        indicatorColor="secondary"
        variant="fullWidth"
        aria-label="full width tabs example"
        style={{ position: "sticky", top: 56, zIndex: 10 }}
      >
        <StyledTab
          label="Details"
          onClick={() => onTabClick("Details")}
          {...a11yProps(0)}
        />

        <StyledTab
          label={
            <StyledBadge badgeContent={""} invisible={hideActivityDot}>
              Activity
            </StyledBadge>
          }
          onClick={() => onTabClick("Activity")}
          {...a11yProps(1)}
        />
      </StyledTabs>

      <SwipeableViews
        axis={theme.direction === "rtl" ? "x-reverse" : "x"}
        index={value}
        onChangeIndex={handleChangeIndex}
      >
        <TabPanel value={value} index={0} dir={theme.direction}>
          <IncidentDetail details={details} />
        </TabPanel>
        <TabPanel value={value} index={1} dir={theme.direction}>
          <ChatPage
            status={details.status}
            groupedChats={groupedSortedChats}
            chats={activity}
            userEmail={userapi.email}
            userId={userapi.userSysId}
            username={userapi.name}
            setComments={setActivity}
            setStatus={setStatus}
            incidentId={id}
            jwt={jwt}
          />
        </TabPanel>
      </SwipeableViews>
    </motion.div>
  );
}

export default IncidentDetailPage;

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          <Typography component={"span"}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}
